import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, ArrowLeft, Loader2, Check, Eye, EyeOff, AlertTriangle } from 'lucide-react'
import { supabase, invokeContactForm } from '@/lib/supabaseClient'
import { toast } from 'sonner'
import { COUNTRY_OPTIONS } from '@/lib/countries'
import { Label } from '@/components/ui/label'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import HeroFishSchool from '@/components/sections/HeroFishSchool'
import { useAuth } from '@/hooks/useAuth'

const STEPS = [
  { id: 1, title: 'Get started' },
  { id: 2, title: 'Project Scope' },
  { id: 3, title: 'Business Details' },
  { id: 4, title: 'New / Existing' },
  { id: 5, title: 'Confirm' },
] as const

const SERVICES = [
  { short: 'Strategy', long: 'Digital Consulting' },
  { short: 'Growth', long: 'Performance Marketing' },
  { short: 'Design', long: 'Brand & Identity' },
  { short: 'Engineering', long: 'Web Development' },
  { short: 'Insights', long: 'Web Analytics' },
  { short: 'Automation', long: 'Artificial Intelligence' },
  { short: 'Authority', long: 'SEO & Content' },
] as const

/** Service-specific drill-down questions with appropriate input types. */
type ScopeQuestionSelect = { type: 'select'; label: string; options: { value: string; label: string }[] }
type ScopeQuestionCheckbox = { type: 'checkbox'; label: string; options: { value: string; label: string }[] }
type ScopeQuestionText = { type: 'text'; label: string; placeholder?: string }
type ScopeQuestionTextarea = { type: 'textarea'; label: string; placeholder?: string }
type ScopeQuestion = ScopeQuestionSelect | ScopeQuestionCheckbox | ScopeQuestionText | ScopeQuestionTextarea

const SERVICE_SCOPE_QUESTIONS: Record<string, ScopeQuestion[]> = {
  Strategy: [
    { type: 'textarea', label: 'What business decisions or challenges are you looking to solve?', placeholder: 'e.g. scaling digital channels, aligning tech with strategy...' },
    { type: 'select', label: 'Do you have an existing digital roadmap?', options: [
      { value: '', label: 'Select...' },
      { value: 'scratch', label: 'Starting from scratch' },
      { value: 'basic', label: 'Have a basic roadmap' },
      { value: 'detailed', label: 'Have a detailed roadmap' },
      { value: 'unsure', label: 'Not sure' },
    ]},
    { type: 'text', label: 'Who are the key stakeholders we should consider?', placeholder: 'e.g. CEO, marketing lead, ops...' },
  ],
  Growth: [
    { type: 'checkbox', label: 'What channels are you currently using?', options: [
      { value: 'paid_search', label: 'Paid search (Google, Bing)' },
      { value: 'paid_social', label: 'Paid social (Meta, LinkedIn, TikTok)' },
      { value: 'organic_social', label: 'Organic social' },
      { value: 'email', label: 'Email marketing' },
      { value: 'affiliates', label: 'Affiliates / partnerships' },
      { value: 'display', label: 'Display / programmatic' },
      { value: 'other', label: 'Other' },
    ]},
    { type: 'select', label: "What's your primary conversion or KPI goal?", options: [
      { value: '', label: 'Select...' },
      { value: 'leads', label: 'Leads / sign-ups' },
      { value: 'sales', label: 'Sales / revenue' },
      { value: 'traffic', label: 'Traffic / awareness' },
      { value: 'engagement', label: 'Engagement / retention' },
      { value: 'other', label: 'Other' },
    ]},
    { type: 'text', label: 'Any target audiences or markets?', placeholder: 'e.g. B2B SaaS, Aus/NZ, 25–45...' },
  ],
  Design: [
    { type: 'select', label: 'Is this a refresh or a new brand?', options: [
      { value: '', label: 'Select...' },
      { value: 'new', label: 'New brand from scratch' },
      { value: 'refresh', label: 'Brand refresh / evolution' },
      { value: 'unsure', label: 'Not sure yet' },
    ]},
    { type: 'text', label: 'What brands or aesthetics do you admire?', placeholder: 'e.g. minimal, bold, playful...' },
    { type: 'checkbox', label: 'Any specific deliverables?', options: [
      { value: 'logo', label: 'Logo' },
      { value: 'guidelines', label: 'Brand guidelines' },
      { value: 'packaging', label: 'Packaging' },
      { value: 'web_design', label: 'Website design' },
      { value: 'social', label: 'Social assets' },
      { value: 'other', label: 'Other' },
    ]},
  ],
  Engineering: [
    { type: 'select', label: 'What type of project?', options: [
      { value: '', label: 'Select...' },
      { value: 'website', label: 'Website' },
      { value: 'web_app', label: 'Web application' },
      { value: 'ecommerce', label: 'E-commerce' },
      { value: 'integration', label: 'Integration / API' },
      { value: 'mobile', label: 'Mobile app' },
      { value: 'other', label: 'Other' },
    ]},
    { type: 'text', label: 'Existing tech stack or platforms to work with?', placeholder: 'e.g. Shopify, React, HubSpot...' },
    { type: 'text', label: 'Must-have features or integrations?', placeholder: 'e.g. CRM sync, payment gateway...' },
  ],
  Insights: [
    { type: 'text', label: 'What metrics or data do you currently track?', placeholder: 'e.g. conversions, traffic, retention...' },
    { type: 'select', label: 'What decisions do you need the data to support?', options: [
      { value: '', label: 'Select...' },
      { value: 'marketing', label: 'Marketing spend / attribution' },
      { value: 'product', label: 'Product decisions' },
      { value: 'growth', label: 'Growth strategy' },
      { value: 'all', label: 'Across the business' },
      { value: 'other', label: 'Other' },
    ]},
    { type: 'checkbox', label: 'Tools you\'re already using?', options: [
      { value: 'ga4', label: 'Google Analytics 4' },
      { value: 'mixpanel', label: 'Mixpanel' },
      { value: 'amplitude', label: 'Amplitude' },
      { value: 'custom', label: 'Custom / data warehouse' },
      { value: 'none', label: 'None yet' },
      { value: 'other', label: 'Other' },
    ]},
  ],
  Automation: [
    { type: 'textarea', label: 'What processes or tasks are you looking to automate?', placeholder: 'e.g. lead routing, reporting, content scheduling...' },
    { type: 'text', label: 'Existing tools or systems to integrate with?', placeholder: 'e.g. Salesforce, Slack, Notion...' },
    { type: 'select', label: 'What would success look like?', options: [
      { value: '', label: 'Select...' },
      { value: 'time_saved', label: 'Time saved / efficiency' },
      { value: 'accuracy', label: 'Accuracy / consistency' },
      { value: 'scale', label: 'Ability to scale' },
      { value: 'cost', label: 'Cost reduction' },
      { value: 'other', label: 'Other' },
    ]},
  ],
  Authority: [
    { type: 'select', label: 'Can your website be found on Google?', options: [
      { value: '', label: 'Select...' },
      { value: 'yes_often', label: 'Yes, we show up when people search for us' },
      { value: 'sometimes', label: 'Sometimes, but not consistently' },
      { value: 'no_unsure', label: 'No or not sure' },
      { value: 'no_website', label: "We don't have a website yet" },
    ]},
    { type: 'text', label: 'What search terms do you want people to find you for?', placeholder: 'e.g. plumbing perth, wedding photographers...' },
    { type: 'text', label: 'Any competitors you’d like to keep up with or outrank?', placeholder: 'e.g. business names or websites...' },
  ],
}

const BUDGET_OPTIONS = [
  { value: '', label: 'Select range...' },
  { value: 'under-5k', label: 'Under $5,000' },
  { value: '5k-10k', label: '$5,000 - $10,000' },
  { value: '10k-25k', label: '$10,000 - $25,000' },
  { value: '25k-50k', label: '$25,000 - $50,000' },
  { value: '50k-100k', label: '$50,000 - $100,000' },
  { value: '100k-plus', label: '$100,000+' },
] as const

const TIMELINE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'asap-deadline', label: 'ASAP — hard deadline coming up' },
  { value: 'within-month', label: 'Within the next month' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'no-deadline', label: 'No hard deadline — exploring timing' },
] as const

type CustomerMode = 'create' | 'signin'

export default function GetStarted() {
  const location = useLocation()
  const { user, loading: authLoading, signIn } = useAuth()
  const [step, setStep] = useState(1)
  const [step1Mode, setStep1Mode] = useState<CustomerMode>('create')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authSubmitting, setAuthSubmitting] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [country, setCountry] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [phone, setPhone] = useState('')
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set())
  const [companyGoals, setCompanyGoals] = useState('')
  const [scopeAnswers, setScopeAnswers] = useState<Record<string, string | string[]>>({})
  const [budget, setBudget] = useState('')
  const [timeline, setTimeline] = useState('')
  const [additionalNotes, setAdditionalNotes] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [setupEmailError, setSetupEmailError] = useState<string | null>(null)
  const [accountSetupRequested, setAccountSetupRequested] = useState(false)
  const [userBusinesses, setUserBusinesses] = useState<{ id: string; name: string; website_url?: string; country?: string; entity_name?: string }[]>([])
  const [userBusinessesLoading, setUserBusinessesLoading] = useState(false)
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | 'new' | null>(null)

  // Sync email and metadata from auth user
  useEffect(() => {
    if (!user) return
    if (user.email) setEmail(user.email)
    if (user.user_metadata?.first_name && !firstName) setFirstName(user.user_metadata.first_name)
    else if (!firstName && user.user_metadata?.full_name) {
      const parts = String(user.user_metadata.full_name).trim().split(/\s+/)
      if (parts[0]) setFirstName(parts[0])
    }
    if (user.user_metadata?.last_name && !lastName) setLastName(user.user_metadata.last_name)
    else if (!lastName && user.user_metadata?.full_name) {
      const parts = String(user.user_metadata.full_name).trim().split(/\s+/)
      if (parts.length > 1) setLastName(parts.slice(1).join(' '))
    }
  }, [user, firstName, lastName])

  // Fetch user's businesses when logged in (from business_memberships)
  useEffect(() => {
    if (!user?.id) {
      setUserBusinesses([])
      setSelectedBusinessId(null)
      return
    }
    setUserBusinessesLoading(true)
    supabase
      .from('business_memberships')
      .select('business_id, businesses(id, name, website_url, country, entity_name)')
      .eq('status', 'active')
      .then(({ data, error }: { data: unknown; error: unknown }) => {
        setUserBusinessesLoading(false)
        if (error) {
          setUserBusinesses([])
          return
        }
        type MembershipRow = { business_id: string; businesses: { id: string; name: string; website_url?: string; country?: string; entity_name?: string } | null }
        const rows = (data ?? []) as MembershipRow[]
        const list = rows.flatMap((row) => {
          const b = row.businesses
          return b ? [{ id: b.id, name: b.name, website_url: b.website_url, country: b.country, entity_name: b.entity_name }] : []
        })
        // Deduplicate by id (user can have multiple memberships to same business)
        const deduped = list.filter((b, i, arr) => arr.findIndex((x) => x.id === b.id) === i)
        setUserBusinesses(deduped)
      })
  }, [user?.id])

  // Pre-select services when entering from contextual links, e.g. /new-project?service=Growth
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const serviceParam = (params.get('service') || '').trim()
    const servicesParam = (params.get('services') || '').trim()
    const candidates = [serviceParam, ...servicesParam.split(',')]
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)

    if (candidates.length === 0) return

    const matches = SERVICES.filter((service) => {
      const short = service.short.toLowerCase()
      const long = service.long.toLowerCase()
      return candidates.some((candidate) => candidate === short || candidate === long)
    }).map((service) => service.short)

    if (matches.length > 0) {
      setSelectedServices(new Set(matches))
    }
  }, [location.search])

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step])

  const canProceedStep1 = true
  const canProceedStep2 = true
  // Business required: either selected existing or new with name + country
  const hasValidBusiness =
    (selectedBusinessId && selectedBusinessId !== 'new' && typeof selectedBusinessId === 'string') ||
    (businessName.trim().length >= 2 && !!country.trim())
  const canProceedStep3 = hasValidBusiness
  const userNeedsNameStep = user && (firstName.trim().length < 2 || lastName.trim().length < 2)
  const canProceedStep4 = user
    ? (userNeedsNameStep ? firstName.trim().length >= 2 && lastName.trim().length >= 2 : true)
    : step1Mode === 'create'
      ? email.trim().includes('@') && firstName.trim().length >= 2 && lastName.trim().length >= 2
      : email.trim().includes('@') && password.trim().length >= 6
  const canProceedStep5 = true
  const NOT_SURE = 'Not sure'
  const selectedServiceList = Array.from(selectedServices).filter((s) => s !== NOT_SURE)
  const hasSpecificServices = selectedServiceList.length > 0

  // Service-specific scope questions for Step 2 (flat list with keys for state)
  const scopeQuestionItems = hasSpecificServices
    ? selectedServiceList.flatMap((service) =>
        (SERVICE_SCOPE_QUESTIONS[service] ?? []).map((q, i) => ({
          service,
          question: q,
          key: `${service}-${i}`,
        }))
      )
    : []

  const setScopeAnswer = (key: string, value: string | string[]) => {
    setScopeAnswers((prev) => ({ ...prev, [key]: value }))
  }

  const getScopeAnswerDisplay = (val: string | string[] | undefined, question: ScopeQuestion): string | null => {
    if (val === undefined || val === null) return null
    if (Array.isArray(val) && val.length > 0) {
      if (question.type === 'checkbox' && 'options' in question) {
        return val.map((v) => question.options.find((o) => o.value === v)?.label ?? v).join(', ')
      }
      return val.join(', ')
    }
    if (typeof val === 'string' && val.trim()) {
      if (question.type === 'select' && val && 'options' in question) {
        return question.options.find((o) => o.value === val)?.label ?? val
      }
      return val.trim()
    }
    return null
  }

  const serializeScopeAnswers = (): string => {
    const parts: string[] = []
    for (const { service, question, key } of scopeQuestionItems) {
      const display = getScopeAnswerDisplay(scopeAnswers[key], question)
      if (!display) continue
      const qLabel = question.label.replace(/\?$/, '')
      parts.push(`${service} – ${qLabel}: ${display}`)
    }
    return parts.join('\n\n')
  }

  // Grouped scope answers for summary display: { service, items: { label, value }[] }
  const scopeAnswersByService = hasSpecificServices
    ? selectedServiceList.map((service) => {
        const items = scopeQuestionItems
          .filter((q) => q.service === service)
          .map(({ question, key }) => {
            const display = getScopeAnswerDisplay(scopeAnswers[key], question)
            return { label: question.label.replace(/\?$/, ''), value: display }
          })
          .filter((item) => item.value)
        return { service, items }
      }).filter((g) => g.items.length > 0)
    : []

  const toggleService = (service: string) => {
    setSelectedServices((prev) => {
      const next = new Set(prev)
      if (service === NOT_SURE) {
        if (next.has(NOT_SURE)) next.delete(NOT_SURE)
        else return new Set([NOT_SURE])
      } else {
        next.delete(NOT_SURE)
        if (next.has(service)) next.delete(service)
        else next.add(service)
      }
      return next
    })
  }

  const handleStep1Auth = async (e: React.FormEvent) => {
    e.preventDefault()
    if (user) return
    setAuthError(null)
    setAuthSubmitting(true)
    try {
      if (step1Mode === 'create') {
        setPassword('')
        setStep(5)
      } else {
        const result = await signIn({ email: email.trim(), password: password.trim(), remember_me: rememberMe })
        if (result.error) {
          setAuthError(result.error)
        } else {
          setPassword('')
          setStep(5)
        }
      }
    } finally {
      setAuthSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (step < 5) {
      setStep((s) => (user && s === 3 && !userNeedsNameStep ? 5 : s + 1))
      return
    }
    setStatus('loading')
    setSubmitError(null)
    setSetupEmailError(null)
    setAccountSetupRequested(!user && step1Mode === 'create')
    try {
      const metadataFullName = (user?.user_metadata?.full_name || '').trim()
      const metadataFullNameParts = metadataFullName ? metadataFullName.split(/\s+/) : []
      const fallbackFirstName = metadataFullNameParts[0] || ''
      const fallbackLastName = metadataFullNameParts.slice(1).join(' ')
      const resolvedFirstName = (firstName.trim() || user?.user_metadata?.first_name?.trim() || fallbackFirstName).trim()
      const resolvedLastName = (lastName.trim() || user?.user_metadata?.last_name?.trim() || fallbackLastName).trim()

      const resolvedEmail = (email.trim() || user?.email || '').trim()
      if (!resolvedFirstName || !resolvedLastName || !resolvedEmail) {
        setAuthError('Please add your first name, last name, and email to continue.')
        setStep(3)
        setStatus('idle')
        return
      }

      const isExistingBusiness = selectedBusinessId && selectedBusinessId !== 'new' && typeof selectedBusinessId === 'string'
      const hasBusinessForSubmit = isExistingBusiness || (businessName.trim().length >= 2 && !!country.trim())
      if (!hasBusinessForSubmit) {
        setSubmitError('Please provide business name and country.')
        setStep(3)
        setStatus('idle')
        return
      }
      const fullName = `${resolvedFirstName} ${resolvedLastName}`.trim()
      const messageText = (hasSpecificServices ? serializeScopeAnswers().trim() : companyGoals.trim() || additionalNotes.trim()) || 'Project enquiry'
      await invokeContactForm({
        // Project brief schema
        first_name: resolvedFirstName,
        last_name: resolvedLastName,
        email: resolvedEmail,
        // Simple contact schema (name, email, message) for compatibility with form handlers that require it
        name: fullName,
        message: messageText,
        phone: phone.trim() || undefined,
        business_id: isExistingBusiness ? selectedBusinessId : undefined,
        business_name: businessName.trim() || undefined,
        country: country || undefined,
        website_url: websiteUrl.trim() || undefined,
        marketing_opt_in: marketingConsent,
        services: Array.from(selectedServices),
        discovery_meeting_only: selectedServices.size === 0,
        company_goals: hasSpecificServices
          ? (serializeScopeAnswers().trim() || undefined)
          : (companyGoals.trim() || undefined),
        budget: budget || undefined,
        timeline: timeline || undefined,
        additional_notes: additionalNotes.trim() || undefined,
      })
      // Update auth user metadata with name for future sessions
      if (user) {
        await supabase.auth.updateUser({
          data: { first_name: resolvedFirstName, last_name: resolvedLastName },
        })
      } else if (step1Mode === 'create') {
        // Email-first flow: send secure link after brief submission.
        const { error: otpError } = await supabase.auth.signInWithOtp({
          email: resolvedEmail.toLowerCase(),
          options: {
            shouldCreateUser: true,
            emailRedirectTo: `${window.location.origin}/complete-account`,
            data: {
              first_name: resolvedFirstName,
              last_name: resolvedLastName,
              full_name: `${resolvedFirstName} ${resolvedLastName}`.trim(),
              marketing_consent: marketingConsent,
            },
          },
        })

        if (otpError) {
          setSetupEmailError(otpError.message)
        }
      }
      setStatus('success')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong'
      setSubmitError(message)
      setStatus('error')
      toast.error(message)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans text-primary selection:bg-accent selection:text-white">
      <Navbar />
      <main className="relative flex-1 pt-32 pb-24 overflow-hidden">
        <HeroFishSchool
          showPredator={false}
          avoidCopy={false}
          fixedToViewport
          preyColor="hsl(211 100% 50%)"
        />
        <div className="absolute inset-0 z-[1] bg-[url('/assets/bg-noise.png')] opacity-[0.035] mix-blend-multiply pointer-events-none" />
        <div className="container relative z-10 max-w-xl md:max-w-2xl mx-auto px-8">
          {/* Progress */}
          <div className="flex justify-center gap-2 mb-12">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={`h-1 flex-1 max-w-24 rounded-full transition-colors ${
                  step >= s.id ? 'bg-accent' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          {status === 'success' ? (
            <div className="text-center space-y-6">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-accent/10 text-accent">
                <Check className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold font-heading text-primary">You're all set</h1>
              {accountSetupRequested ? (
                <p className="text-slate-600 text-lg">
                  Thanks for reaching out. We sent you a secure email link to complete your account setup.
                </p>
              ) : (
                <p className="text-slate-600 text-lg">
                  Thanks for reaching out. We'll be in touch soon.
                </p>
              )}
              {setupEmailError && (
                <p className="text-sm text-amber-700">
                  Your brief was submitted, but we couldn&apos;t send the setup email right now. You can still sign in from the client portal.
                </p>
              )}
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-accent font-medium hover:underline"
              >
                Back to home
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : status === 'error' ? (
            <div className="text-center space-y-6">
              <h1 className="text-3xl font-bold font-heading text-primary">Something went wrong</h1>
              {submitError && (
                <p className="text-amber-700 text-sm font-medium">{submitError}</p>
              )}
              <p className="text-slate-600">
                Try again or{' '}
                <a href="mailto:hello@gigafish.co" className="text-accent underline">
                  email us directly
                </a>
                .
              </p>
              <button
                type="button"
                onClick={() => { setStatus('idle'); setSubmitError(null) }}
                className="text-accent font-medium hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (step === 4 && !user) {
                  await handleStep1Auth(e)
                } else {
                  handleSubmit(e)
                }
              }}
              className="space-y-8"
            >
              {/* Step 1: Services */}
              {step === 1 && (
                <div className="space-y-6 animate-fade-up">
                  <div>
                    <h1 className="text-3xl font-bold font-heading text-primary">Start a new project</h1>
                    <p className="text-slate-500 text-sm mt-2">
                      Select the areas you&apos;re interested in, or skip to continue.
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600">Which areas interest you?</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-heading uppercase tracking-wide rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-slate-500">
                          Optional
                        </span>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm">
                      Helps us prepare. Tap any that apply, or skip.
                    </p>
                    <div className="space-y-2">
                      {SERVICES.map((service) => (
                        <label
                          key={service.short}
                          className={`flex items-start gap-4 cursor-pointer group p-3 rounded-lg border transition-colors ${
                            selectedServices.has(service.short)
                              ? 'border-accent bg-accent/5'
                              : 'border-slate-200 bg-slate-50/80 hover:bg-slate-50'
                          }`}
                        >
                          <span className="relative flex shrink-0 mt-0.5">
                            <input
                              type="checkbox"
                              checked={selectedServices.has(service.short)}
                              onChange={() => toggleService(service.short)}
                              className="sr-only"
                            />
                            <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${
                              selectedServices.has(service.short)
                                ? 'border-accent bg-accent'
                                : 'border-slate-300 bg-white group-hover:border-slate-400'
                            }`}>
                              {selectedServices.has(service.short) && <Check className="h-3 w-3 text-white stroke-[3] animate-check-pop" />}
                            </span>
                          </span>
                          <span>
                            <span className="text-slate-700 group-hover:text-primary transition-colors text-sm font-medium block">{service.short}</span>
                            <span className="text-slate-400 text-xs block">{service.long}</span>
                          </span>
                        </label>
                      ))}
                    </div>

                    <hr className="border-0 border-t border-dotted border-slate-200 my-4" />

                    <label
                      className={`flex items-start gap-4 cursor-pointer group p-3 rounded-lg border transition-colors ${
                        selectedServices.has(NOT_SURE)
                          ? 'border-accent bg-accent/5'
                          : 'border-slate-200 bg-slate-50/80 hover:bg-slate-50'
                      }`}
                    >
                      <span className="relative flex shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          checked={selectedServices.has(NOT_SURE)}
                          onChange={() => toggleService(NOT_SURE)}
                          className="sr-only"
                        />
                        <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${
                          selectedServices.has(NOT_SURE)
                            ? 'border-accent bg-accent'
                            : 'border-slate-300 bg-white group-hover:border-slate-400'
                        }`}>
                          {selectedServices.has(NOT_SURE) && <Check className="h-3 w-3 text-white stroke-[3] animate-check-pop" />}
                        </span>
                      </span>
                      <span>
                        <span className="text-slate-700 group-hover:text-primary transition-colors text-sm font-medium block">Not sure</span>
                        <span className="text-slate-400 text-xs block">We&apos;ll help identify where we can add the most value.</span>
                      </span>
                    </label>

                  </div>
                </div>
              )}

              {/* Step 2: Project scope */}
              {step === 2 && (
                <div className="space-y-6 animate-fade-up">
                  <div>
                    <h1 className="text-3xl font-bold font-heading text-primary">Project scope</h1>
                    <p className="text-slate-500 text-sm mt-2">Share just enough context so we can recommend the right next step.</p>
                  </div>

                  {hasSpecificServices ? (
                    selectedServiceList.map((service) => {
                      const items = scopeQuestionItems.filter((q) => q.service === service)
                      return (
                        <div key={service} className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-heading uppercase tracking-wide text-slate-600">{service}</h4>
                            <span className="text-[10px] font-heading uppercase tracking-wide rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-slate-500">
                              Optional
                            </span>
                          </div>
                          {items.map(({ service: _s, question, key }) => (
                          <div key={key} className="space-y-2">
                            <Label htmlFor={`scope-${key}`} className="text-base font-medium text-slate-700">
                              {question.label}
                            </Label>
                            {question.type === 'select' && (
                              <select
                                id={`scope-${key}`}
                                value={(scopeAnswers[key] as string) ?? ''}
                                onChange={(e) => setScopeAnswer(key, e.target.value)}
                                className="select-chevron-on-light w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              >
                                {question.options.map((opt) => (
                                  <option key={opt.value} value={opt.value} className="bg-white text-primary">
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            )}
                            {question.type === 'checkbox' && (
                              <div className="space-y-2">
                                {question.options.map((opt) => {
                                  const selected = ((scopeAnswers[key] as string[]) ?? []).includes(opt.value)
                                  return (
                                    <label
                                      key={opt.value}
                                      className={`flex items-center gap-3 cursor-pointer p-4 rounded-lg border transition-colors ${
                                        selected
                                          ? 'border-accent bg-accent/5'
                                          : 'border-slate-200 bg-slate-50/80 hover:bg-slate-50'
                                      }`}
                                    >
                                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all ${selected ? 'border-accent bg-accent' : 'border-slate-300 bg-white'}`}>
                                        {selected && <Check className="h-3 w-3 text-white stroke-[3] animate-check-pop" />}
                                      </span>
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => {
                                          const prev = (scopeAnswers[key] as string[]) ?? []
                                          const next = prev.includes(opt.value)
                                            ? prev.filter((v) => v !== opt.value)
                                            : [...prev, opt.value]
                                          setScopeAnswer(key, next)
                                        }}
                                        className="sr-only"
                                      />
                                      <span className="text-slate-700">{opt.label}</span>
                                    </label>
                                  )
                                })}
                              </div>
                            )}
                            {question.type === 'text' && (
                              <input
                                id={`scope-${key}`}
                                type="text"
                                value={(scopeAnswers[key] as string) ?? ''}
                                onChange={(e) => setScopeAnswer(key, e.target.value)}
                                placeholder={question.placeholder}
                                className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              />
                            )}
                            {question.type === 'textarea' && (
                              <textarea
                                id={`scope-${key}`}
                                value={(scopeAnswers[key] as string) ?? ''}
                                onChange={(e) => setScopeAnswer(key, e.target.value)}
                                placeholder={question.placeholder}
                                rows={2}
                                className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all resize-none"
                              />
                            )}
                          </div>
                          ))}
                        </div>
                      )
                    })
                  ) : (
                    <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600">Project scope</h3>
                        <span className="text-[10px] font-heading uppercase tracking-wide rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-slate-500">
                          Optional
                        </span>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wizard-goals" className="text-base font-medium text-slate-700">
                          What are you trying to achieve?
                        </Label>
                      <textarea
                        id="wizard-goals"
                        value={companyGoals}
                        onChange={(e) => setCompanyGoals(e.target.value)}
                        placeholder="e.g. grow revenue, launch a product, improve brand awareness, scale our marketing..."
                        rows={3}
                        className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all resize-none"
                      />
                      </div>
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600">Project particulars</h3>
                      <span className="text-[10px] font-heading uppercase tracking-wide rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-slate-500">
                        Optional
                      </span>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="wizard-budget" className="text-base font-medium text-slate-700">
                          Project budget
                        </Label>
                        <p className="text-slate-400 text-xs mb-1">
                          Estimated budget for this project — helps us recommend the right approach.
                        </p>
                        <select
                          id="wizard-budget"
                          value={budget}
                          onChange={(e) => setBudget(e.target.value)}
                          className="select-chevron-on-light w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                        >
                          {BUDGET_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value} className="bg-white text-primary">
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wizard-timeline" className="text-base font-medium text-slate-700">
                          When do you need this live?
                        </Label>
                        <p className="text-slate-400 text-xs mb-1">
                          Target launch or deadline — helps us recommend the right pace.
                        </p>
                        <select
                          id="wizard-timeline"
                          value={timeline}
                          onChange={(e) => setTimeline(e.target.value)}
                          className="select-chevron-on-light w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                        >
                          {TIMELINE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value} className="bg-white text-primary">
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wizard-notes" className="text-base font-medium text-slate-700">
                          Anything else we should know?
                        </Label>
                        <textarea
                          id="wizard-notes"
                          value={additionalNotes}
                          onChange={(e) => setAdditionalNotes(e.target.value)}
                          placeholder="e.g. current challenges, key stakeholders, must-haves..."
                          rows={2}
                          className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Business details */}
              {step === 3 && (
                <div className="space-y-6 animate-fade-up">
                  <div>
                    <h1 className="text-3xl font-bold font-heading text-primary">Business details</h1>
                    <p className="text-slate-500 text-sm mt-2">
                      {user && userBusinesses.length > 0
                        ? 'Select an existing business or add a new one for this project.'
                        : 'Add the business context we should keep on file for this brief.'}
                    </p>
                  </div>

                  {user && userBusinesses.length > 0 ? (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                        <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600">Select business</h3>
                        {userBusinessesLoading ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {userBusinesses.map((b) => (
                              <label
                                key={b.id}
                                className={`flex items-start gap-4 cursor-pointer group p-4 rounded-lg border transition-colors ${
                                  selectedBusinessId === b.id
                                    ? 'border-accent bg-accent/5'
                                    : 'border-slate-200 bg-slate-50/80 hover:bg-slate-50'
                                }`}
                              >
                                <span className="relative flex shrink-0 mt-0.5">
                                  <input
                                    type="radio"
                                    name="business-select"
                                    checked={selectedBusinessId === b.id}
                                    onChange={() => {
                                      setSelectedBusinessId(b.id)
                                      setBusinessName(b.name)
                                      setWebsiteUrl(b.website_url ?? '')
                                      setCountry(b.country ?? '')
                                    }}
                                    className="peer sr-only"
                                  />
                                  <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${
                                    selectedBusinessId === b.id ? 'border-accent bg-accent' : 'border-slate-300 bg-white'
                                  }`}>
                                    {selectedBusinessId === b.id && <span className="h-2 w-2 rounded-full bg-white" />}
                                  </span>
                                </span>
                                <div>
                                  <span className="text-accent font-medium block">{b.name}</span>
                                  {(b.entity_name || b.country) && (
                                    <span className="text-slate-500 text-sm block mt-0.5">{b.entity_name || b.country}</span>
                                  )}
                                </div>
                              </label>
                            ))}
                            <label
                              className={`flex items-start gap-4 cursor-pointer group p-4 rounded-lg border transition-colors ${
                                selectedBusinessId === 'new'
                                  ? 'border-accent bg-accent/5'
                                  : 'border-slate-200 bg-slate-50/80 hover:bg-slate-50'
                              }`}
                            >
                              <span className="relative flex shrink-0 mt-0.5">
                                <input
                                  type="radio"
                                  name="business-select"
                                  checked={selectedBusinessId === 'new'}
                                  onChange={() => {
                                    setSelectedBusinessId('new')
                                    setBusinessName('')
                                    setWebsiteUrl('')
                                    setCountry('')
                                  }}
                                  className="peer sr-only"
                                />
                                <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${
                                  selectedBusinessId === 'new' ? 'border-accent bg-accent' : 'border-slate-300 bg-white'
                                }`}>
                                  {selectedBusinessId === 'new' && <span className="h-2 w-2 rounded-full bg-white" />}
                                </span>
                              </span>
                              <span className="text-accent font-medium">Add new business</span>
                            </label>
                          </div>
                        )}
                      </div>

                      {selectedBusinessId === 'new' && (
                        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                          <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600">New business details <span className="text-amber-500">*</span></h3>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="wizard-business" className="text-base font-medium text-slate-700">Business name <span className="text-amber-500">*</span></Label>
                              <input
                                id="wizard-business"
                                type="text"
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                placeholder="Your company or business"
                                className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="wizard-country-new" className="text-base font-medium text-slate-700">Country <span className="text-amber-500">*</span></Label>
                              <select
                                id="wizard-country-new"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="select-chevron-on-light w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              >
                                <option value="" className="bg-white text-primary">Select country...</option>
                                {COUNTRY_OPTIONS.map((countryName) => (
                                  <option key={countryName} value={countryName} className="bg-white text-primary">{countryName}</option>
                                ))}
                              </select>
                              {country === 'Other' && (
                                <p className="flex items-start gap-2 text-xs text-amber-700">
                                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                  <span>We usually partner with clients in the listed markets. If you&apos;re based elsewhere, you can still submit and we&apos;ll confirm whether we&apos;re the right fit.</span>
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="wizard-website" className="text-base font-medium text-slate-700">Website URL</Label>
                              <input
                                id="wizard-website"
                                type="url"
                                value={websiteUrl}
                                onChange={(e) => setWebsiteUrl(e.target.value)}
                                placeholder="https://yourdomain.com"
                                className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600">Business details</h3>
                        <span className="text-[10px] font-heading uppercase tracking-wide rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-slate-500">Required</span>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wizard-business" className="text-base font-medium text-slate-700">Business name <span className="text-amber-500">*</span></Label>
                        <input
                          id="wizard-business"
                          type="text"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Your company or business"
                          autoFocus
                          className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wizard-country" className="text-base font-medium text-slate-700">Country <span className="text-amber-500">*</span></Label>
                        <select
                          id="wizard-country"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          className="select-chevron-on-light w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                        >
                          <option value="" className="bg-white text-primary">Select country...</option>
                          {COUNTRY_OPTIONS.map((countryName) => (
                            <option key={countryName} value={countryName} className="bg-white text-primary">{countryName}</option>
                          ))}
                        </select>
                        {country === 'Other' && (
                          <p className="flex items-start gap-2 text-xs text-amber-700">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>We usually partner with clients in the listed markets. If you&apos;re based elsewhere, you can still submit and we&apos;ll confirm whether we&apos;re the right fit.</span>
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wizard-website" className="text-base font-medium text-slate-700">Website URL</Label>
                        <input
                          id="wizard-website"
                          type="url"
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                          placeholder="https://yourdomain.com"
                          className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: New/Existing customer (guests) or Your details (logged-in, name missing) */}
              {step === 4 && (!user || userNeedsNameStep) && (
                <div className="space-y-6 animate-fade-up">
                  {!user ? (
                    <>
                  <div>
                    <h1 className="text-3xl font-bold font-heading text-primary">New or existing customer</h1>
                    <p className="text-slate-500 text-sm mt-2">Choose your customer type so we can route your submission correctly.</p>
                  </div>
                  {authLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="relative flex gap-2 p-1 rounded-lg bg-slate-100 border border-slate-200">
                        <motion.div
                          className="absolute top-1 bottom-1 rounded-md bg-white"
                          layoutId="auth-toggle-bg"
                          style={{ width: 'calc(50% - 4px)' }}
                          animate={{ left: step1Mode === 'create' ? 4 : 'calc(50% + 0px)' }}
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        />
                        <button
                          type="button"
                          onClick={() => { setStep1Mode('create'); setAuthError(null); setPassword('') }}
                          className={`relative z-10 flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                            step1Mode === 'create' ? 'text-accent' : 'text-slate-500 hover:text-primary'
                          }`}
                        >
                          New customer
                        </button>
                        <button
                          type="button"
                          onClick={() => { setStep1Mode('signin'); setAuthError(null) }}
                          className={`relative z-10 flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                            step1Mode === 'signin' ? 'text-accent' : 'text-slate-500 hover:text-primary'
                          }`}
                        >
                          Existing customer
                        </button>
                      </div>

                      {step1Mode === 'create' ? (
                        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                          <h3 className="text-xs font-heading uppercase tracking-wide text-slate-500">User details</h3>
                          <div className="space-y-2">
                            <Label htmlFor="wizard-email" className="text-base font-medium text-slate-700">
                              Email address <span className="text-amber-500">*</span>
                            </Label>
                            <input
                              id="wizard-email"
                              type="email"
                              value={email}
                              onChange={(e) => { setEmail(e.target.value); setAuthError(null) }}
                              placeholder="you@company.com"
                              required
                              autoFocus
                              className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="wizard-first-name" className="text-base font-medium text-slate-700">
                                First name <span className="text-amber-500">*</span>
                              </Label>
                              <input
                                id="wizard-first-name"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="First name"
                                required
                                minLength={2}
                                className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="wizard-last-name" className="text-base font-medium text-slate-700">
                                Last name <span className="text-amber-500">*</span>
                              </Label>
                              <input
                                id="wizard-last-name"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Last name"
                                required
                                minLength={2}
                                className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wizard-phone" className="text-base font-medium text-slate-700">
                              Phone number <span className="text-slate-400">(optional)</span>
                            </Label>
                            <input
                              id="wizard-phone"
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder="+61 4xx xxx xxx"
                              className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                            />
                          </div>

                          <label className="mt-6 pt-5 flex items-center gap-3 cursor-pointer group border-t border-slate-200">
                            <span className="relative flex shrink-0">
                              <input
                                id="wizard-marketing-consent"
                                type="checkbox"
                                checked={marketingConsent}
                                onChange={(e) => setMarketingConsent(e.target.checked)}
                                className="peer sr-only"
                              />
                              <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${marketingConsent ? 'border-accent bg-accent' : 'border-slate-300 bg-white group-hover:border-slate-400'}`}>
                                {marketingConsent && <Check className="h-3 w-3 text-white stroke-[3] animate-check-pop" />}
                              </span>
                            </span>
                            <span className="text-slate-700 text-sm leading-snug">
                              I'd like to receive marketing and product updates via email.
                            </span>
                          </label>
                        </div>
                      ) : (
                        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                          <h3 className="text-xs font-heading uppercase tracking-wide text-slate-500">Login details</h3>
                          <div className="space-y-2">
                            <Label htmlFor="wizard-email" className="text-base font-medium text-slate-700">
                              Email address <span className="text-amber-500">*</span>
                            </Label>
                            <input
                              id="wizard-email"
                              type="email"
                              value={email}
                              onChange={(e) => { setEmail(e.target.value); setAuthError(null) }}
                              placeholder="you@company.com"
                              required
                              autoFocus
                              className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wizard-password" className="text-base font-medium text-slate-700">
                              Password <span className="text-amber-500">*</span>
                            </Label>
                            <div className="relative">
                              <input
                                id="wizard-password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setAuthError(null) }}
                                placeholder="Your password"
                                required
                                minLength={6}
                                className="w-full px-6 py-4 pr-12 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>

                          <label className="mt-6 pt-5 flex items-center gap-3 cursor-pointer group border-t border-slate-200">
                            <span className="relative flex shrink-0">
                              <input
                                id="wizard-remember-me"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="peer sr-only"
                              />
                              <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${rememberMe ? 'border-accent bg-accent' : 'border-slate-300 bg-white group-hover:border-slate-400'}`}>
                                {rememberMe && <Check className="h-3 w-3 text-white stroke-[3] animate-check-pop" />}
                              </span>
                            </span>
                            <span className="text-slate-700 text-sm leading-snug">
                              Remember me on this device
                            </span>
                          </label>
                        </div>
                      )}

                      {authError && (
                        <p className="text-sm text-red-600">{authError}</p>
                      )}
                    </div>
                  )}
                  </>
                    ) : (
                      <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8 space-y-4">
                        <div>
                          <h1 className="text-3xl font-bold font-heading text-primary">Your details</h1>
                          <p className="text-slate-500 text-sm mt-2">We need your name for the project brief.</p>
                        </div>
                        {authError && (
                          <p className="text-sm text-red-600">{authError}</p>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="wizard-first-name-step4" className="text-base font-medium text-slate-700">First name <span className="text-amber-500">*</span></Label>
                            <input
                              id="wizard-first-name-step4"
                              type="text"
                              value={firstName}
                              onChange={(e) => { setFirstName(e.target.value); setAuthError(null) }}
                              placeholder="First name"
                              className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wizard-last-name-step4" className="text-base font-medium text-slate-700">Last name <span className="text-amber-500">*</span></Label>
                            <input
                              id="wizard-last-name-step4"
                              type="text"
                              value={lastName}
                              onChange={(e) => { setLastName(e.target.value); setAuthError(null) }}
                              placeholder="Last name"
                              className="w-full px-6 py-4 rounded-lg border border-slate-200 bg-white text-primary placeholder:text-slate-400 focus:outline-none hover:border-slate-300 focus:border-accent/40 transition-all"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-base font-medium text-slate-700">Email</Label>
                          <p className="text-slate-600">{email}</p>
                        </div>
                      </div>
                    )}
                  </div>
              )}

              {/* Step 5: Confirm */}
              {step === 5 && (
                <div className="space-y-6 animate-fade-up">
                  <div>
                    <h1 className="text-3xl font-bold font-heading text-primary">Confirm details</h1>
                    <p className="text-slate-500 text-sm mt-2">
                      Review your details before submitting.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Services */}
                    <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                      <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600 mb-4">Services</h3>
                      <p className="font-medium text-primary">
                        {selectedServices.size > 0 ? Array.from(selectedServices).join(', ') : '—'}
                      </p>
                    </div>

                    {/* Project scope — service-specific or generic goals */}
                    {scopeAnswersByService.length > 0 ? (
                      scopeAnswersByService.map(({ service, items }) => (
                        <div key={service} className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                          <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600 mb-4">{service}</h3>
                          <dl className="space-y-3">
                            {items.map(({ label, value }) => (
                              <div key={label}>
                                <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">{label}</dt>
                                <dd className="font-medium text-primary">{value}</dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ))
                    ) : companyGoals.trim() && (
                      <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                        <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600 mb-4">Project scope</h3>
                        <div className="space-y-1">
                          <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Goals</dt>
                          <dd className="font-medium text-primary whitespace-pre-wrap">{companyGoals}</dd>
                        </div>
                      </div>
                    )}

                    {/* Project particulars */}
                    {(budget || timeline || additionalNotes.trim()) && (
                      <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                        <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600 mb-4">Project particulars</h3>
                        <dl className="space-y-3">
                          <div>
                            <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Budget</dt>
                            <dd className="font-medium text-primary">
                              {budget ? (BUDGET_OPTIONS.find((o) => o.value === budget)?.label ?? budget) : '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Timeline</dt>
                            <dd className="font-medium text-primary">
                              {timeline ? (TIMELINE_OPTIONS.find((o) => o.value === timeline)?.label ?? timeline) : '—'}
                            </dd>
                          </div>
                          {additionalNotes.trim() && (
                            <div>
                              <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Additional notes</dt>
                              <dd className="font-medium text-primary whitespace-pre-wrap">{additionalNotes}</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    )}

                    {/* Business details */}
                    {(businessName.trim() || websiteUrl.trim() || country) && (
                      <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                        <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600 mb-4">Business details</h3>
                        <dl className="space-y-3">
                          {businessName.trim() && (
                            <div>
                              <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Business</dt>
                              <dd className="font-medium text-primary">{businessName}</dd>
                            </div>
                          )}
                          {websiteUrl.trim() && (
                            <div>
                              <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Website</dt>
                              <dd className="font-medium text-primary break-all">{websiteUrl}</dd>
                            </div>
                          )}
                          {country && (
                            <div>
                              <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Country</dt>
                              <dd className="font-medium text-primary">{country}</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    )}

                    {/* User details */}
                    <div className="rounded-xl border border-slate-200 bg-white p-5 lg:p-8">
                      <h3 className="text-sm font-heading uppercase tracking-wide text-slate-600 mb-4">Contact details</h3>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Name</dt>
                          <dd className="font-medium text-primary">{`${firstName} ${lastName}`.trim() || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Email</dt>
                          <dd className="font-medium text-primary break-all">{email}</dd>
                        </div>
                        {phone.trim() && (
                          <div>
                            <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">Phone</dt>
                            <dd className="font-medium text-primary">{phone}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-4 pt-4">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={() => setStep((s) => (user && s === 5 && !userNeedsNameStep ? 3 : s - 1))}
                    className="flex items-center gap-2 px-6 py-3 text-slate-700 hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                ) : (
                  <Link
                    to="/"
                    className="flex items-center gap-2 px-6 py-3 text-slate-500 hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Cancel
                  </Link>
                )}
                <button
                  type="submit"
                  disabled={
                    status === 'loading' ||
                    (step === 1 && (!canProceedStep1 || authSubmitting)) ||
                    (step === 2 && !canProceedStep2) ||
                    (step === 3 && !canProceedStep3) ||
                    (step === 4 && !canProceedStep4) ||
                    (step === 5 && !canProceedStep5)
                  }
                  className="ml-auto flex items-center gap-2 px-8 py-3 rounded-lg bg-accent text-white font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : step === 4 && authSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : step === 4 && !user && step1Mode === 'signin' ? (
                    <>
                      Sign in
                      <ArrowRight className="h-4 w-4" />
                    </>
                  ) : step < 5 ? (
                    <>
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Submit
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}
